import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  GET_SWING_CONTRACT_PAYLOAD_INFO,
  SWING_CONTRACT_MESSAGES,
} from '../../constants/FieldNomsSwingContract';
import { get } from '../../services/GenesisApi';
import {
  GetFieldAuditLogsParams,
  IFieldAuditLogs,
  IFieldType,
  ILogsState,
} from '../../types/FieldNomsSwingContract.type';
import { sendNotification } from '../../UI/Notification';

type GetFieldAuditLogsType = ({
  url,
  hasLogs,
  fieldType,
  origin,
}: GetFieldAuditLogsParams) => ILogsState & {
  resetLogsOnCloseModal: () => void;
  onExpandLogs: (id: number) => void;
  onPayloadLogs?: (auditLog: IFieldAuditLogs) => void;
};

const useGetFieldAuditLogs: GetFieldAuditLogsType = ({
  url,
  hasLogs = false,
  fieldType,
}: GetFieldAuditLogsParams) => {
  const initialAuditLogs = {
    hasLoading: false,
    auditLogs: [],
  };
  const [{ hasLoading, auditLogs }, setAuditLogs] = useState<ILogsState>(initialAuditLogs);

  const handleResetAuditLogsOnClose = () => {
    setAuditLogs((prevState) => ({
      ...prevState,
      auditLogs: [],
    }));
  };

  const handleExpandCollapseOnClick = useCallback(
    (id: number) => {
      setAuditLogs((prevState) => ({
        ...prevState,
        auditLogs: prevState.auditLogs.map((log) => {
          if (log.id === id) {
            return {
              ...log,
              expanded: !log.expanded,
            };
          }
          return log;
        }),
      }));
    },
    [setAuditLogs],
  );

  const getAuditLogs = useCallback(
    (apiUrl: string) => {
      setAuditLogs((prevState) => ({
        ...prevState,
        hasLoading: true,
      }));
      get(apiUrl)
        .then((response) => {
          const { data = [] } = response;
          const mappedData = data.map((log) => {
            const baseLog = {
              id: log.id,
              note: log.note,
              expanded: false,
            };

            if (fieldType === IFieldType.SWING_CONTRACT) {
              return {
                ...baseLog,
                setaPayload: null,
                createdDateTimeinGMT: log.createdDateTimeInGmt,
                payload: log.payload,
              };
            }

            return {
              ...baseLog,
              setaPayload:
                log.auditLog === 'null' || log.auditLog === undefined ? null : log.auditLog,
              createdDateTimeinGMT: log.modifiedDate,
            };
          });

          setAuditLogs((prevState) => ({
            ...prevState,
            auditLogs: mappedData,
          }));
        })
        .catch(() => {
          sendNotification({
            type: 'error',
            message: SWING_CONTRACT_MESSAGES.SERVER_ERROR,
          });
        })
        .finally(() => {
          setAuditLogs((prevState) => ({
            ...prevState,
            hasLoading: false,
          }));
        });
    },
    [fieldType],
  );

  const getFieldFormsPayloadLogs = (auditLog: IFieldAuditLogs): void => {
    const updateLogWithPayload = (response: { data?: Record<string, unknown> }): void => {
      const updatedLogs = auditLogs.map((log) => {
        if (log.id === auditLog.id) {
          return {
            ...log,
            expanded: true,
            setaPayloadLoading: false,
            setaPayload: response?.data || {},
          };
        }
        return log;
      });
      setAuditLogs({ ...initialAuditLogs, auditLogs: updatedLogs });
    };

    const handleError = (): void => {
      sendNotification({
        type: 'error',
        message: SWING_CONTRACT_MESSAGES.SERVER_ERROR,
      });
      const updatedLogs = auditLogs.map((log) => ({
        ...log,
        setaPayloadLoading: false,
      }));
      setAuditLogs({ ...initialAuditLogs, auditLogs: updatedLogs });
    };

    get(`${GET_SWING_CONTRACT_PAYLOAD_INFO}?trackerId=${auditLog.payload?.id}`)
      .then(updateLogWithPayload)
      .catch(handleError);
  };

  const handleOnOpenPayload = (auditLog: IFieldAuditLogs) => {
    if (auditLog.setaPayload && !auditLog.expanded) {
      handleExpandCollapseOnClick(auditLog.id);
    }
    if (auditLog.setaPayload) {
      return;
    }
    setAuditLogs((prevState) => ({
      ...prevState,
      auditLogs: prevState.auditLogs.map((log) => {
        if (log.id === auditLog.id) {
          return {
            ...log,
            setaPayloadLoading: true,
            expanded: true,
          };
        }
        return log;
      }),
    }));
    getFieldFormsPayloadLogs(auditLog);
  };

  const sortedAuditLogsWithLatestLogs = useMemo(() => {
    return auditLogs.sort((logA, logB) => logB.id - logA.id);
  }, [auditLogs]);

  useEffect(() => {
    if (hasLogs) {
      getAuditLogs(url);
    }
  }, [hasLogs, url, getAuditLogs]);

  return {
    hasLoading,
    auditLogs: sortedAuditLogsWithLatestLogs,
    resetLogsOnCloseModal: handleResetAuditLogsOnClose,
    onExpandLogs: handleExpandCollapseOnClick,
    onPayloadLogs: handleOnOpenPayload,
  };
};
export default useGetFieldAuditLogs;
